import type { IEncodingVersionAdapter } from '../../../../core/encodingCompatibility';

/**
 * 旧版本到新版本的适配器（v1 -> v2）
 * 将 { name, value: [x, y, size?], group } 转换为 { color, x, y, size?, group }
 */
export const dynamicBubbleLegacyAdapter: IEncodingVersionAdapter = {
  version: 'v1-to-v2',

  /**
   * 判断是否为旧版本编码格式
   * @param encoding - 编码对象
   * @returns 是否匹配旧版本格式
   */
  isMatch: (encoding: Record<string, unknown>): boolean => {
    const hasLegacyFields =
      typeof encoding.name === 'string' &&
      Array.isArray(encoding.value) &&
      typeof encoding.group === 'string';

    const lacksNewFields =
      encoding.color === undefined && encoding.x === undefined && encoding.y === undefined;

    return hasLegacyFields && lacksNewFields;
  },

  /**
   * 将旧版本编码转换为新版本格式
   * @param encoding - 旧版本编码对象
   * @returns 新版本编码对象
   */
  transform: (encoding: Record<string, unknown>): Record<string, unknown> => {
    const { name, value, group, ...rest } = encoding;

    // 旧版本中 name 对应新版本的 color
    // 旧版本中 value 是数组 [x, y, size?]
    const valueArray = value as string[];
    const [x, y, size] = valueArray;

    const result: Record<string, unknown> = {
      color: name,
      x,
      y,
      group,
      ...rest
    };

    if (size) {
      result.size = size;
    }

    return result;
  },

  /**
   * 将新版本编码反向转换为旧版本格式
   * @param encoding - 新版本编码对象
   * @returns 旧版本编码对象
   */
  reverse: (encoding: Record<string, unknown>): Record<string, unknown> => {
    const { color, x, y, size, group, ...rest } = encoding;

    // 将 x, y, size 组合成 value 数组
    const value = size ? [x, y, size] : [x, y];

    return {
      name: color,
      value,
      group,
      ...rest
    };
  }
};

/**
 * 新版本适配器（v2）
 * 直接通过，无需转换
 */
export const dynamicBubbleCurrentAdapter: IEncodingVersionAdapter = {
  version: 'v2',

  /**
   * 判断是否为新版本编码格式
   * @param encoding - 编码对象
   * @returns 是否匹配新版本格式
   */
  isMatch: (encoding: Record<string, unknown>): boolean => {
    const hasNewFields =
      typeof encoding.color === 'string' &&
      typeof encoding.x === 'string' &&
      typeof encoding.y === 'string' &&
      typeof encoding.group === 'string';

    const lacksLegacyFields = encoding.name === undefined && encoding.value === undefined;

    return hasNewFields && lacksLegacyFields;
  },

  /**
   * 直接返回编码，无需转换
   * @param encoding - 编码对象
   * @returns 原始编码对象
   */
  transform: (encoding: Record<string, unknown>): Record<string, unknown> => {
    return encoding;
  }
};

/**
 * 获取动态气泡图的所有适配器
 * @returns 适配器数组
 */
export function getDynamicBubbleAdapters(): IEncodingVersionAdapter[] {
  return [dynamicBubbleLegacyAdapter, dynamicBubbleCurrentAdapter];
}
